import React, { useEffect, useRef, useState } from "react";

import { Link } from "react-router-dom";
import crossover_logo from "../../assets/nba.svg";
import sports from "../../sports";
import styles from "./GuessThePlayer.module.css";
import playerData from "./NBAMEGA.json";
import playerSchedule from "./playerSchedule2.json";
import moment from "moment-timezone";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useRandomPlayer } from "./guesser/useRandomPlayer.ts";

const Icon = {
  home: (p) => (<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" {...p}><path d="M12 3l9 8h-3v9h-4v-6h-4v6H6v-9H3z"/></svg>),
  settings: (p) => (<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" {...p}><path d="M19.14 12.94c.04-.31.06-.62.06-.94 0-.32-.02-.63-.06-.94l2.03-1.58a.5.5 0 00.12-.64l-1.92-3.32a.5.5 0 00-.61-.22l-2.39.96a7 7 0 00-1.62-.94l-.36-2.54A.5.5 0 0014 2h-4a.5.5 0 00-.49.42l-.36 2.54c-.59.24-1.13.55-1.62.94l-2.39-.96a.5.5 0 00-.61.22L2.61 8.48a.5.5 0 00.12.64L4.76 10.7c-.04.31-.06.62-.06.94s.02.63.06.94l-2.03 1.58a.5.5 0 00-.12.64l1.92 3.32c.14.24.43.34.61.22l2.39-.96c.49.39 1.03.7 1.62.94l.36 2.54a.5.5 0 00.49.42h4a.5.5 0 00.49-.42l.36-2.54c.59-.24 1.13-.55 1.62-.94l2.39.96c.18.12.47.02.61-.22l1.92-3.32a.5.5 0 00-.12-.64zM12 15.5a3.5 3.5 0 110-7 3.5 3.5 0 010 7z"/></svg>),
  help: (p) => (<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" {...p}><path d="M12 2a10 10 0 100 20 10 10 0 000-20zm.95 14.95h-1.9v-1.9h1.9zm1.97-7.43l-.86.88c-.69.7-1.11 1.27-1.11 2.55h-1.9v-.5c0-.94.42-1.79 1.11-2.49l1.18-1.2a1.9 1.9 0 00-1.34-3.26 1.9 1.9 0 00-1.9 1.9H7.2a3.8 3.8 0 117.72.92z"/></svg>),
  edit: (p) => (<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...p}><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75z"/></svg>),
  filter: (p) => (<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...p}><path d="M10 18h4v-2h-4zM3 6v2h18V6zm3 7h12v-2H6z"/></svg>),
  close: (p) => (<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" {...p}><path d="M19 6.4L17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12z"/></svg>),
};

const Modal = ({ open, onClose, children, wide }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={`${styles.modalContent} ${wide ? styles.modalWide : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {onClose && (
          <button className={styles.modalClose} onClick={onClose} aria-label="Close">
            <Icon.close />
          </button>
        )}
        {children}
      </div>
    </div>
  );
};

const GuessThePlayer = () => {
  // MUI Button Styles
  const primaryButtonSx = {
    background: 'linear-gradient(135deg, #4666af 0%, #27408b 100%)',
    color: 'white',
    border: 'none',
    padding: '14px 28px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    boxShadow: '0 4px 12px rgba(70, 102, 175, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #27408b 0%, #1a2d5f 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(70, 102, 175, 0.4)',
    },
  };

  const secondaryButtonSx = {
    background: 'rgba(70, 102, 175, 0.1)',
    color: '#4666af',
    border: '2px solid rgba(70, 102, 175, 0.3)',
    padding: '10px 20px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    boxShadow: '0 2px 8px rgba(70, 102, 175, 0.15)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'rgba(70, 102, 175, 0.15)',
      borderColor: 'rgba(70, 102, 175, 0.5)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(70, 102, 175, 0.2)',
    },
  };

  const containerSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '32px 20px',
    minHeight: '100vh',
  };

  const cardSx = {
    backgroundColor: 'background.paper',
    padding: '32px',
    borderRadius: '20px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
    border: '1px solid',
    borderColor: 'divider',
    width: '100%',
    maxWidth: '800px',
    margin: '20px auto',
  };

  const sectionSx = {
    marginBottom: '24px',
  };

  const infoBlockSx = {
    padding: '16px',
    marginBottom: '16px',
    backgroundColor: 'rgba(70, 102, 175, 0.05)',
    borderRadius: '12px',
    border: '1px solid rgba(70, 102, 175, 0.2)',
  };

  const [mode, setMode] = useState("Daily"); // Mode can be 'Daily' or 'Unlimited'
  const {randomPlayer: currentPlayer} = useRandomPlayer();

  // const [currentPlayer, setCurrentPlayer] = useState(null);
  const [guessesLeft, setGuessesLeft] = useState(3);
  const [userGuess, setUserGuess] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [currentDate, setCurrentDate] = useState(
    moment().tz("America/New_York").format("YYYY-MM-DD")
  );
  const [playedToday, setPlayedToday] = useState(
    localStorage.getItem(currentDate) === "played"
  );
  const [hintsGiven, setHintsGiven] = useState([]);
  const [page_name, setPageName] = useState(""); // Define page_name state
  const [selectedTeams, setSelectedTeams] = useState(new Set());
  const [currentPlayerId, setCurrentPlayerId] = useState(null);
  const hasDraftDetails =
    currentPlayer &&
    (currentPlayer.Round || currentPlayer.Pick || currentPlayer.DrftYr);
  // Additional state within the GuessThePlayer component
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [showNoPlayersModal, setShowNoPlayersModal] = useState(false);

  const [playerIDInput, setPlayerIDInput] = useState(
    localStorage.getItem("playerID") || ""
  );
  const [seenPlayerIDs, setSeenPlayerIDs] = useState(new Set());
  const [outOfPlayers, setOutOfPlayers] = useState(false);
  const [showEditPlayerIDModal, setShowEditPlayerIDModal] = useState(false);
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [selectedAwards, setSelectedAwards] = useState(new Set());
  const [tempSelectedAwards, setTempSelectedAwards] = useState(
    new Set(selectedAwards)
  );
  const awards = [
    "Hart_Memorial",
    "Lady_Byng",
    "Vezina",
    "Calder",
    "Art_Ross",
    "Norris_Memorial",
    "Conn_Smythe",
    "Masterton_Memorial",
    "Ted_Lindsay",
    "Frank_Selke",
    "Jennings",
    "Clancy_Memorial",
    "Maurice_Richard",
    "Mark_Messier",
  ]; // Add all relevant awards
  const toggleEditPlayerIDModal = () => {
    setShowEditPlayerIDModal((prev) => !prev);
  };
  const EditPlayerIDModal = ({ open, onClose }) => {
    const [tempPlayerID, setTempPlayerID] = useState(playerIDInput);

    const handleSave = () => {
      savePlayerID(tempPlayerID);
      onClose(); // Close modal after save
    };

    useEffect(() => {
      if (open) {
        setTempPlayerID(playerIDInput); // Reset local state when modal opens
      }
    }, [open]);

    return (
      <Modal open={open} onClose={onClose}>
        <h2 className={styles.modalTitle}>Edit Player ID</h2>
        <label className={styles.fieldLabel}>
          <span>Player ID</span>
          <input
            type="text"
            className={styles.input}
            value={tempPlayerID}
            onChange={(e) => setTempPlayerID(e.target.value)}
          />
        </label>
        <div className={styles.modalActions}>
          <button className={styles.btnPrimary} onClick={handleSave}>Save</button>
          <button className={styles.btnGhost} onClick={onClose}>Cancel</button>
        </div>
      </Modal>
    );
  };

  const [selectedPositions, setSelectedPositions] = useState(new Set());
  const [isActiveOnly, setIsActiveOnly] = useState("both");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tempSelectedPositions, setTempSelectedPositions] = useState(new Set());
  const [tempSelectedTeams, setTempSelectedTeams] = useState(new Set());
  const [draftStatus, setDraftStatus] = useState("both"); // drafted, undrafted, both
  const [minGames, setMinGames] = useState(0); // Minimum number of games played
  const [tempDraftStatus, setTempDraftStatus] = useState("both");
  const [tempMinGames, setTempMinGames] = useState(0);

  const [tempIsActiveOnly, setTempIsActiveOnly] = useState("both");
  // Track different statistics for each game mode
  const [homeTotalGames, setHomeTotalGames] = useState(
    parseInt(localStorage.getItem("homeTotalGames") || 0)
  );
  const [homeTotalWins, setHomeTotalWins] = useState(
    parseInt(localStorage.getItem("homeTotalWins") || 0)
  );
  const [homeWinStreak, setHomeWinStreak] = useState(
    parseInt(localStorage.getItem("homeWinStreak") || 0)
  );
  const [unlimitedTotalGames, setUnlimitedTotalGames] = useState(
    parseInt(localStorage.getItem("unlimitedTotalGames") || 0)
  );
  const [unlimitedTotalWins, setUnlimitedTotalWins] = useState(
    parseInt(localStorage.getItem("unlimitedTotalWins") || 0)
  );
  const [unlimitedWinStreak, setUnlimitedWinStreak] = useState(
    parseInt(localStorage.getItem("unlimitedWinStreak") || 0)
  );
  const [homeHighestStreak, setHomeHighestStreak] = useState(
    parseInt(localStorage.getItem("homeHighestStreak") || 0)
  );
  const [unlimitedHighestStreak, setUnlimitedHighestStreak] = useState(
    parseInt(localStorage.getItem("unlimitedHighestStreak") || 0)
  );
  const [isDarkMode, setIsDarkMode] = useState(false);
  const teams = [
    "AND",
    "ATL",
    "BAL",
    "BLB",
    "BOS",
    "BRK",
    "BUF",
    "CAP",
    "CHA",
    "CHH",
    "CHI",
    "CHO",
    "CHP",
    "CHS",
    "CHZ",
    "CIN",
    "CLE",
    "CLR",
    "DAL",
    "DEN",
    "DET",
    "DNN",
    "DTF",
    "FTW",
    "GSW",
    "HOU",
    "IND",
    "INJ",
    "INO",
    "KCK",
    "KCO",
    "LAC",
    "LAL",
    "MEM",
    "MIA",
    "MIL",
    "MIN",
    "MLH",
    "MNL",
    "NJN",
    "NOH",
    "NOJ",
    "NOK",
    "NOP",
    "NYK",
    "NYN",
    "OKC",
    "ORL",
    "PHI",
    "PHO",
    "PHW",
    "PIT",
    "POR",
    "PRO",
    "ROC",
    "SAC",
    "SAS",
    "SDC",
    "SDR",
    "SEA",
    "SFW",
    "SHE",
    "STB",
    "STL",
    "SYR",
    "TOR",
    "TRH",
    "TRI",
    "UTA",
    "VAN",
    "WAS",
    "WAT",
    "WSB",
    "WSC",
  ];
  const [currentPage, setCurrentPage] = useState(1);
  
  const [hasNextPage, setHasNextPage] = useState(true);

  useEffect(() => {
    const filters = {
      teams: [...selectedTeams],
      positions: [...selectedPositions],
      isActiveOnly,
      draftStatus,
      minGames,
      awards: [...selectedAwards], // Include selected awards in stored filters
    };
    localStorage.setItem("filters", JSON.stringify(filters));
  }, [
    selectedTeams,
    selectedPositions,
    isActiveOnly,
    draftStatus,
    minGames,
    selectedAwards,
  ]); // Add selectedAwards as a dependency

  useEffect(() => {
    const loadedFilters = JSON.parse(localStorage.getItem("filters")) || {};
    setSelectedTeams(new Set(loadedFilters.teams || []));
    setSelectedPositions(new Set(loadedFilters.positions || []));
    setIsActiveOnly(loadedFilters.isActiveOnly || "both");
    setDraftStatus(loadedFilters.draftStatus || "both");
    setMinGames(loadedFilters.minGames || 0);
    setSelectedAwards(new Set(loadedFilters.awards || [])); // Load selected awards from stored filters
  }, []); // No dependencies to trigger this effect initially

  const fetchLeaderboard = async (next = false) => {
    let nextPage = next ? currentPage + 1 : 1; // Increment or reset to first page
    let url = `https://us-central1-crossover-grid-511a4.cloudfunctions.net/api/getLeaderboard?page=${nextPage}`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      if (response.ok) {
        if (next && data.leaderboard.length > 0) {
          setLeaderboard((prevLeaderboard) => [
            ...prevLeaderboard,
            ...data.leaderboard,
          ]);
          setCurrentPage(nextPage);
        } else if (!next) {
          setLeaderboard(data.leaderboard);
          setCurrentPage(1);
        }
        setHasNextPage(data.hasNextPage); // Update hasNextPage state based on the API response
      } else {
        toast.error(`Error: ${data.message || "Failed to fetch leaderboard"}`);
        setLeaderboard([]);
        setHasNextPage(false); // Assume no more pages if an error occurs
      }
    } catch (error) {
      toast.error(`Network error: ${error.toString()}`);
      setHasNextPage(false); // Assume no more pages if a network error occurs
    }
  };

  const [showHowToPlay, setShowHowToPlay] = useState(false);

  const toggleHowToPlayModal = () => {
    setShowHowToPlay((prev) => !prev);
  };

  const HowToPlayModal = ({ open, onClose }) => (
    <Modal open={open} onClose={onClose}>
      <h2 className={styles.modalTitle}>How to Play</h2>
      <p className={styles.modalLead}>Ready to test your player knowledge? Here's what you need to do:</p>
      <ul className={styles.modalList}>
          <li>
            <strong>🚀 Pick a mode:</strong> Choose between 'Daily' or
            'Unlimited' to start playing.
          </li>
          <li>
            <strong>🛠 Set your filters:</strong> Fine-tune the list of players
            by selecting specific teams, positions, or statuses to make your
            players more manageable.
          </li>
          <li>
            <strong>🔍 Make your guess:</strong> Type the name of who you think
            it is into the input field and see if you're right.
          </li>
          <li>
            <strong>💡 Use hints:</strong> If your first guess isn’t quite
            right, we’ll give you some hints like position and draft round to
            help steer you in the right direction.
          </li>
          <li>
            <strong>🏆 Keep your streak alive:</strong> Guess as many as you can
            in a row. Also, don’t forget to save your username to compete on the
            leaderboard where your highest streak will appear!
          </li>
        </ul>
      <div className={styles.modalActions}>
        <button className={styles.btnPrimary} onClick={onClose}>Got it, let's play!</button>
      </div>
    </Modal>
  );

  const openLeaderboardModal = () => {
    fetchLeaderboard(); // Fetch with the initial page
    setShowLeaderboardModal(true);
  };


  const closeLeaderboardModal = () => {
    setShowLeaderboardModal(false);
  };


  const createMarkup = (htmlContent) => {
    return { __html: htmlContent };
  };

  const savePlayerID = (newPlayerID) => {
    if (
      leaderboard.some(
        (item) => item.playerID.toString() === newPlayerID.toString()
      ) ||
      newPlayerID.trim() !== ""
    ) {
      setPlayerIDInput(newPlayerID); // Update the global state
      localStorage.setItem("playerID", newPlayerID);
      alert("Player ID saved!");
    } else {
      alert("Player ID does not exist in the leaderboard!");
    }
  };
  const LeaderboardModal = ({
    open,
    onClose,
    leaderboard,
    fetchMoreLeaderboard,
  }) => {
    const [isLoading, setIsLoading] = useState(false);
    const loaderRef = useRef(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !isLoading) {
            setIsLoading(true);
            fetchLeaderboard(true).then(() => setIsLoading(false)); // Set loading state to false after fetching
          }
        },
        { threshold: 1.0 }
      );

      if (loaderRef.current) {
        observer.observe(loaderRef.current);
      }

      return () => observer.disconnect();
    }, [loaderRef.current, isLoading]);

    if (!open) return null;

    return (
      <div className={styles.modal} onClick={onClose}>
        <div
          className={styles.leaderboardModalContainer}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className={styles.modalTitle}>Leaderboard</h2>
          <div className={styles.leaderboardModalContent}>
            <table className={styles.leaderboardTable}>
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Longest Streak</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((row, index) => (
                  <tr key={index}>
                    <td>{row.playerID}</td>
                    <td>{row.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {isLoading && (
              <div className={styles.loadingIndicator}>Loading...</div>
            )}
            <button
              className={styles.loadMoreButton}
              onClick={() => fetchLeaderboard(true)}
              disabled={isLoading}
            >
              Load More Scores
            </button>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  };

  const teamLogos = {
    "ATLANTA HAWKS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/atl.png&h=200&w=200",
    CELTICS:
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/bos.png&h=200&w=200",
    "BROOKLYN NETS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/bkn.png&h=200&w=200",
    "CHARLOTTE HORNETS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/cha.png&h=200&w=200",
    "CHICAGO BULLS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/chi.png&h=200&w=200",
    "CLEVELAND CAVALIERS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/cle.png&h=200&w=200",
    "DALLAS MAVERICKS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/dal.png&h=200&w=200",
    "DENVER NUGGETS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/den.png&h=200&w=200",
    "DETROIT PISTONS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/det.png&h=200&w=200",
    "GOLDEN STATE WARRIORS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/gs.png&h=200&w=200",
    "HOUSTON ROCKETS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/hou.png&h=200&w=200",
    "INDIANA PACERS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/ind.png&h=200&w=200",
    CLIPPERS:
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/lac.png&h=200&w=200",
    LAKERS:
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/lal.png&h=200&w=200",
    "GRIZZLIES MEMPHIS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/mem.png&h=200&w=200",
    "MIAMI HEAT":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/mia.png&h=200&w=200",
    "MILWAUKEE BUCKS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/mil.png&h=200&w=200",
    "MINNESOTA TIMBERWOLVES":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/min.png&h=200&w=200",
    "NEW ORLEANS PELICANS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/no.png&h=200&w=200",
    "NEW YORK KNICKS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/ny.png&h=200&w=200",
    "OKC THUNDER":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/okc.png&h=200&w=200",
    "ORLANDO MAGIC":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/orl.png&h=200&w=200",
    "PHILADELPHIA 76ERS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/phi.png&h=200&w=200",
    "PHOENIX SUNS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/phx.png&h=200&w=200",
    "PORTLAND TRAIL BLAZERS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/por.png&h=200&w=200",
    "SACRAMENTO KINGS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/sac.png&h=200&w=200",
    "SAN ANTONIO SPURS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/sa.png&h=200&w=200",
    "TORONTO RAPTORS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/tor.png&h=100&w=100",
    "UTAH JAZZ":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/utah.png",
    "WASHINGTON WIZARDS":
      "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/wsh.png&h=200&w=200",
    BRK: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/bkn.png&h=200&w=200",
    CHA: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/cha.png&h=200&w=200",
    HOU: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/hou.png&h=200&w=200",
    IND: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/ind.png&h=200&w=200",
    LAL: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/lal.png&h=200&w=200",
    MEM: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/mem.png&h=200&w=200",
    MIA: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/mia.png&h=200&w=200",
    MIL: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/mil.png&h=200&w=200",
    MIN: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/min.png&h=200&w=200",
    OKC: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/okc.png&h=200&w=200",
    ORL: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/orl.png&h=200&w=200",
    POR: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/por.png&h=200&w=200",
    TOR: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/tor.png&h=100&w=100",
    UTA: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/utah.png",
    ATL: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/atl.png&h=200&w=200",
    NYK: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/ny.png&h=200&w=200",
    GSW: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/gs.png&h=200&w=200",
    CLE: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/cle.png&h=200&w=200",
    BOS: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/bos.png&h=200&w=200",
    CHI: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/chi.png&h=200&w=200",
    DAL: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/dal.png&h=200&w=200",
    DEN: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/den.png&h=200&w=200",
    DET: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/det.png&h=200&w=200",
    LAC: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/lac.png&h=200&w=200",
    NOP: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/no.png&h=200&w=200",
    SAC: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/sac.png&h=200&w=200",
    SAS: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/sa.png&h=200&w=200",
    WAS: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/wsh.png&h=200&w=200",
    PHI: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/phi.png&h=200&w=200",
    PHO: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/nba/500/phx.png&h=200&w=200",
    SEA: "https://upload.wikimedia.org/wikipedia/en/thumb/a/a4/Seattle_SuperSonics_logo.svg/1200px-Seattle_SuperSonics_logo.svg.png",
    NJN: "https://sportslogohistory.com/wp-content/uploads/2016/SLH/nba_primary/new_jersey_nets.png",
    BLB: "https://1000logos.net/wp-content/uploads/2018/06/Baltimore-Bullets-logo.png",
    CHO: "https://upload.wikimedia.org/wikipedia/en/thumb/c/c4/Charlotte_Hornets_%282014%29.svg/440px-Charlotte_Hornets_%282014%29.svg.png",
    NOH: "https://content.sportslogos.net/logos/6/226/full/new_orleans_hornets_logo_primary_20093313.png",
    NOJ: "https://content.sportslogos.net/logos/6/255/full/new_orleans_jazz_logo__9217.png",
    VAN: "https://upload.wikimedia.org/wikipedia/en/thumb/1/1e/Vancouver_Grizzlies_logo.svg/1200px-Vancouver_Grizzlies_logo.svg.png",
    WSB: "https://1000logos.net/wp-content/uploads/2018/06/Baltimore-Bullets-logo.png",
    SDC: "https://seeklogo.com/images/S/san-diego-clippers-logo-5F664EC47E-seeklogo.com.png",
  };

  const toggleTeam = (team) => {
    // This assumes there is no distinction between temporary and final state,
    // which might not always be appropriate for settings dialogs.
    setTempSelectedTeams((prevTeams) => {
      const newTeams = new Set(prevTeams);
      if (newTeams.has(team)) {
        newTeams.delete(team);
      } else {
        newTeams.add(team);
      }
      return newTeams;
    });

    setSelectedTeams((prev) => {
      const updatedTeams = new Set(prev);
      if (updatedTeams.has(team)) {
        updatedTeams.delete(team);
      } else {
        updatedTeams.add(team);
      }
      return updatedTeams;
    });
  };

  useEffect(() => {
    // Check if the user prefers dark mode
    const prefersDarkMode =
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    setIsDarkMode(prefersDarkMode);

    // Listen for changes in the dark mode preference
    const mediaQueryListener = (e) => {
      setIsDarkMode(e.matches);
    };

    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addListener(mediaQueryListener);

    return () => {
      window
        .matchMedia("(prefers-color-scheme: dark)")
        .removeListener(mediaQueryListener);
    };
  }, []);
  useEffect(() => {
    // Apply or remove the dark-mode class based on the isDarkMode state
    if (isDarkMode) {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }
  }, [isDarkMode]);

  const onAdRefresh = () => {
    window.freestar?.queue.push(function () {
      window.freestar?.newStickyFooter("crossovergrid_sticky_footer");
      window.freestar?.refresh("crossovergrid_sidewall_left");
      window.freestar?.refresh("crossovergrid_sidewall_right");
    });
  };

  // Function to handle year range change and automatically close menu
  useEffect(() => {
    onAdRefresh();
  }, []);

  useEffect(() => {
    // This effect should only run for changes in currentDate and mode, specifically when in Daily mode.
    if (mode === "Daily") {
      const playerId = playerSchedule[currentDate];
      if (playerId) {
        const scheduledPlayer = playerData.find(
          (p) => p["Player-additional"] === playerId
        );
        if (scheduledPlayer) {
          // TODO: replace this 
          // setCurrentPlayer(scheduledPlayer);
        } else {
          // TODO: replace this 
          // setCurrentPlayer(
          //   playerData[Math.floor(Math.random() * playerData.length)]
          // );
        }
      } else {
        // TODO: replace this 
          // setCurrentPlayer(
          // playerData[Math.floor(Math.random() * playerData.length)]
        // );
      }
    }
  }, [currentDate, mode]);

  useEffect(() => {
    // This will refresh the currentDate at 7:00 AM EST every day
    const now = moment().tz("America/New_York");
    const tomorrow = now.clone().add(1, "days").startOf("day");
    const msUntil7AM = tomorrow.add(7, "hours").diff(now);

    const timer = setTimeout(() => {
      setCurrentDate(moment().tz("America/New_York").format("YYYY-MM-DD"));
      setPlayedToday(localStorage.getItem(currentDate) === "played");
    }, msUntil7AM);

    return () => clearTimeout(timer);
  }, [currentDate]);

  useEffect(() => {
    // Fetch player data on mount or when mode or date changes
    const playerId = playerSchedule[currentDate];
    const scheduledPlayer = playerData.find(
      (p) => p["Player-additional"] === playerId
    );
    // TODO: replace this 
          // setCurrentPlayer(
    //   scheduledPlayer ||
    //     playerData[Math.floor(Math.random() * playerData.length)]
    // );
    setPlayedToday(localStorage.getItem(currentDate) === "played");
  }, [mode, currentDate]);
  useEffect(() => {
    localStorage.setItem("homeHighestStreak", homeHighestStreak.toString());
    localStorage.setItem(
      "unlimitedHighestStreak",
      unlimitedHighestStreak.toString()
    );
  }, [homeHighestStreak, unlimitedHighestStreak]);

  const handleModeChange = () => {
    const newMode = mode === "Daily" ? "Unlimited" : "Daily";
    setMode(newMode);
    resetGameState();
  };

  const resetGameState = () => {
    setGuessesLeft(3);
    setUserGuess("");
    setSuggestions([]);
    setHintsGiven([]);
    setSeenPlayerIDs(new Set()); // Optionally reset seen IDs on mode change
    fetchPlayer(mode); // Ensure this fetches according to the new mode
  };

  const generatePlayerID = () => {
    return "id-" + Math.random().toString(36).substr(2, 9); // This creates a random alphanumeric string prefixed with 'id-'
  };

  const updateWinsAndStreak = () => {
    const updateWins =
      mode === "Daily" ? setHomeTotalWins : setUnlimitedTotalWins;
    const updateStreak =
      mode === "Daily" ? setHomeWinStreak : setUnlimitedWinStreak;
    const updateHighestStreak =
      mode === "Daily" ? setHomeHighestStreak : setUnlimitedHighestStreak;

    updateWins((wins) => wins + 1);
    updateStreak((streak) => {
      const newStreak = streak + 1;
      if (
        newStreak >
        (mode === "Daily" ? homeHighestStreak : unlimitedHighestStreak)
      ) {
        updateHighestStreak(newStreak);
        localStorage.setItem(
          mode === "Daily" ? "homeHighestStreak" : "unlimitedHighestStreak",
          newStreak.toString()
        );
      }
      return newStreak;
    });
  };

  const resetStreak = async () => {
    const currentStreak = mode === "Daily" ? homeWinStreak : unlimitedWinStreak;

    // Retrieve player ID from storage or generate a new one if not found
    let playerID = localStorage.getItem("playerID");
    if (!playerID) {
      playerID = generatePlayerID();
      localStorage.setItem("playerID", playerID); // Optionally store the newly generated ID back to local storage
    }

    // Post streak to leaderboard if a streak exists
    if (currentStreak > 0) {
      await postStreakToLeaderboard(playerID, currentStreak).catch((error) => {
        toast.error("Error updating leaderboard: " + error.message);
      });
    }

    // Reset the streak
    const reset = mode === "Daily" ? setHomeWinStreak : setUnlimitedWinStreak;
    reset(0); // Reset the streak
  };
  useEffect(() => {
    const storedMode = localStorage.getItem("gameMode");
    if (storedMode) {
      setMode(storedMode);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("gameMode", mode);
  }, [mode]);

  const updateGamesPlayed = () => {
    const updateGames =
      mode === "Daily" ? setHomeTotalGames : setUnlimitedTotalGames;
    updateGames((games) => games + 1);
  };
  const formatFilters = () => {
    const positionFilters = [...selectedPositions].join(", ");
    const teamFilters = [...selectedTeams].map((team) => team).join(", ");
    const statusFilter =
      isActiveOnly === "both"
        ? "Active and Inactive"
        : isActiveOnly === "active"
          ? "Active"
          : "Inactive";
    const draftFilter =
      draftStatus === "both" ? "Drafted and Undrafted" : draftStatus;
    const gamesFilter = `Minimum games: ${minGames}`;
    const awardFilters = [...selectedAwards].join(", "); // Include selected awards in the formatted filters

    return `Positions: ${positionFilters || "All"}, Teams: ${teamFilters || "All"}, Status: ${statusFilter}, Draft: ${draftFilter}, ${gamesFilter}, Awards: ${awardFilters || "All"}`; // Include award filters in the returned string
  };

  const shareAchievement = (playerCount) => {
    const filtersDescription = formatFilters();
    const message = `I've guessed every available player under the current filters in Guess the Player! Total players guessed: ${playerCount}. Filters used: ${filtersDescription}. Can you match me?`;

    // Include social media or clipboard sharing functionality
    // This example uses Twitter sharing
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`;
    window.open(tweetUrl, "_blank");
    toast.success("Achievement shared on Twitter!");
  };
  const formatCareerAwards = (player) => {
    const awards = [];
    // New awards with emojis and bolded
    if (player.Hart_Memorial && player.Hart_Memorial.length)
      awards.push(
        `🏆 <strong>Hart Memorial:</strong> ${player.Hart_Memorial.join(", ")}`
      );
    if (player.Lady_Byng && player.Lady_Byng.length)
      awards.push(
        `🎩 <strong>Lady Byng:</strong> ${player.Lady_Byng.join(", ")}`
      );
    if (player.Vezina && player.Vezina.length)
      awards.push(`🥅 <strong>Vezina:</strong> ${player.Vezina.join(", ")}`);
    if (player.Calder && player.Calder.length)
      awards.push(`🏅 <strong>Calder:</strong> ${player.Calder.join(", ")}`);
    if (player.Art_Ross && player.Art_Ross.length)
      awards.push(
        `🎨 <strong>Art Ross:</strong> ${player.Art_Ross.join(", ")}`
      );
    if (player.Norris_Memorial && player.Norris_Memorial.length)
      awards.push(
        `🛡️ <strong>Norris Memorial:</strong> ${player.Norris_Memorial.join(", ")}`
      );
    if (player.Conn_Smythe && player.Conn_Smythe.length)
      awards.push(
        `🏒 <strong>Conn Smythe:</strong> ${player.Conn_Smythe.join(", ")}`
      );
    if (player.Masterton_Memorial && player.Masterton_Memorial.length)
      awards.push(
        `🥇 <strong>Masterton Memorial:</strong> ${player.Masterton_Memorial.join(", ")}`
      );
    if (player.Ted_Lindsay && player.Ted_Lindsay.length)
      awards.push(
        `🚀 <strong>Ted Lindsay:</strong> ${player.Ted_Lindsay.join(", ")}`
      );
    if (player.Frank_Selke && player.Frank_Selke.length)
      awards.push(
        `🔒 <strong>Frank Selke:</strong> ${player.Frank_Selke.join(", ")}`
      );
    if (player.Jennings && player.Jennings.length)
      awards.push(
        `🎖️ <strong>Jennings:</strong> ${player.Jennings.join(", ")}`
      );
    if (player.Clancy_Memorial && player.Clancy_Memorial.length)
      awards.push(
        `🤠 <strong>Clancy Memorial:</strong> ${player.Clancy_Memorial.join(", ")}`
      );
    if (player.Maurice_Richard && player.Maurice_Richard.length)
      awards.push(
        `🚨 <strong>Maurice Richard:</strong> ${player.Maurice_Richard.join(", ")}`
      );
    if (player.Mark_Messier && player.Mark_Messier.length)
      awards.push(
        `💪 <strong>Mark Messier:</strong> ${player.Mark_Messier.join(", ")}`
      );

    return awards.map((award) => `<p>${award}</p>`).join("");
  };
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const getFilteredRandomPlayer = () => {
    const players = playerData.filter((player) => {
      const matchesPosition =
        selectedPositions.size === 0 ||
        (player.Pos && player.Pos.some((pos) => selectedPositions.has(pos)));
      const matchesTeam =
        selectedTeams.size === 0 ||
        (player.Team && player.Team.some((team) => selectedTeams.has(team)));
      const matchesActiveStatus =
        isActiveOnly === "both" ||
        (isActiveOnly === "active"
          ? player.Active === "Yes"
          : player.Active === "No");
      const matchesDraftStatus =
        draftStatus === "both" ||
        (draftStatus === "drafted" ? player.Drafted : !player.Drafted);
      const matchesGameCount = player.G >= minGames;
      const matchesAwards = Array.from(selectedAwards).every(
        (award) => Array.isArray(player[award]) && player[award].length > 0
      );
      const notSeen = !seenPlayerIDs.has(player["Player-additional"]);

      return (
        matchesPosition &&
        matchesTeam &&
        matchesActiveStatus &&
        matchesDraftStatus &&
        matchesGameCount &&
        matchesAwards &&
        notSeen
      );
    });

    setFilteredPlayers(players); // Update the state with the filtered players
    if (players.length === 0) {
      setOutOfPlayers(true);
      return null;
    } else {
      setOutOfPlayers(false);
      return players[Math.floor(Math.random() * players.length)];
    }
  };

  // Define a function to toggle the selection of an award
  const toggleAwardSelection = (award) => {
    const updatedAwards = new Set(tempSelectedAwards);
    if (updatedAwards.has(award)) {
      updatedAwards.delete(award);
    } else {
      updatedAwards.add(award);
    }
    setTempSelectedAwards(updatedAwards);
  };

  useEffect(() => {
    getFilteredRandomPlayer(); // Call this whenever filters change to refresh the list
  }, [
    selectedPositions,
    selectedTeams,
    isActiveOnly,
    draftStatus,
    minGames,
    selectedAwards,
  ]); // Include selectedAwards in the dependency array

  const checkAndHandleCompletion = () => {
    // Only check for completion in Unlimited mode
    if (
      mode === "Unlimited" &&
      seenPlayerIDs.size === filteredPlayers.length &&
      filteredPlayers.length > 0
    ) {
      toast.info(
        "Congratulations! You have guessed every available player under the current filters!",
        {
          onClose: () => shareAchievement(seenPlayerIDs.size),
        }
      );
      setShowNoPlayersModal(true); // Trigger the modal when all players are guessed.
    }
  };

  // Call this in an effect hook that watches changes in the necessary dependencies
  useEffect(() => {
    if (mode === "Unlimited") {
      checkAndHandleCompletion();
    }
  }, [seenPlayerIDs, filteredPlayers, mode]); // Include 'mode' to react to mode changes

  const fetchPlayer = (modeToUse) => {
    if (modeToUse === "Daily") {
      // Fetch the scheduled player
      const playerId = playerSchedule[currentDate];
      const scheduledPlayer = playerData.find(
        (p) => p["Player-additional"] === playerId
      );
      if (scheduledPlayer) {
        // TODO: replace this 
          // setCurrentPlayer(scheduledPlayer);
      } else {
        // TODO: replace this 
          // setCurrentPlayer(playerData[Math.floor(Math.random() * playerData.length)]);
      }
    } else {
      // 'Unlimited' mode, fetch any random player considering filters if applied
      const randomPlayer = getFilteredRandomPlayer(); // Ensure this function considers all active filters
      if (randomPlayer) {
        // TODO: replace this 
          // setCurrentPlayer(randomPlayer);
      } else {
        // TODO: replace this 
          // setCurrentPlayer(playerData[Math.floor(Math.random() * playerData.length)]);
      }
    }
  };

  const saveSettings = () => {
    // Check if there are changes to save
    const hasChanges =
      !setsAreEqual(new Set(selectedTeams), new Set(tempSelectedTeams)) ||
      !setsAreEqual(
        new Set(selectedPositions),
        new Set(tempSelectedPositions)
      ) ||
      !setsAreEqual(new Set(selectedAwards), new Set(tempSelectedAwards)) || // Check for changes in awards
      isActiveOnly !== tempIsActiveOnly ||
      draftStatus !== tempDraftStatus ||
      minGames !== Number(tempMinGames);

    if (hasChanges) {
      // Update states with new settings
      setSelectedTeams(new Set(tempSelectedTeams));
      setSelectedPositions(new Set(tempSelectedPositions));
      setSelectedAwards(new Set(tempSelectedAwards)); // Update awards state
      setIsActiveOnly(tempIsActiveOnly);
      setDraftStatus(tempDraftStatus);
      setMinGames(Number(tempMinGames));

      // Persist changes to local storage
      localStorage.setItem(
        "selectedTeams",
        JSON.stringify([...tempSelectedTeams])
      );
      localStorage.setItem(
        "selectedPositions",
        JSON.stringify([...tempSelectedPositions])
      );
      localStorage.setItem(
        "selectedAwards",
        JSON.stringify([...tempSelectedAwards])
      ); // Save awards
      localStorage.setItem("isActiveOnly", tempIsActiveOnly);
      localStorage.setItem("draftStatus", tempDraftStatus);
      localStorage.setItem("minGames", Number(tempMinGames).toString());

      // Close settings and refresh data
      setSettingsOpen(false);
      fetchPlayer(mode);
      setSeenPlayerIDs(new Set()); // Clear seen player IDs when switching modes
    } else {
      setSettingsOpen(false);
    }
  };

  function setsAreEqual(setA, setB) {
    if (setA.size !== setB.size) return false;
    for (let a of setA) {
      if (!setB.has(a)) return false;
    }
    return true;
  }


  useEffect(() => {
    fetchPlayer(mode);
  }, [
    mode,
    currentDate,
    selectedTeams,
    selectedPositions,
    isActiveOnly,
    draftStatus,
    minGames,
    selectedAwards,
  ]); // Include all relevant filters as dependencies

  useEffect(() => {
    const loadedTeams = JSON.parse(localStorage.getItem("selectedTeams")) || [];
    setSelectedTeams(new Set(loadedTeams));
  }, []);

  useEffect(() => {
    fetchPlayer(mode);
  }, [mode, currentDate]);

  useEffect(() => {
    if (mode === "Unlimited") {
      fetchPlayer("Unlimited");
    }
  }, [mode, selectedPositions, isActiveOnly, selectedTeams, selectedAwards]); // Include all relevant filters as dependencies

  const togglePositionSelection = (pos) => {
    const newPositions = new Set(tempSelectedPositions);
    if (newPositions.has(pos)) {
      newPositions.delete(pos);
    } else {
      newPositions.add(pos);
    }
    setTempSelectedPositions(newPositions);
  };

  const handleOpenSettings = (open) => {
    const currentStreak = mode === "Daily" ? homeWinStreak : unlimitedWinStreak;
    if (currentStreak > 0) {
      alert("You cannot change filters during an active streak!");
      return;
    }
    setSettingsOpen(open);
    // Initialize temporary states with current settings
    setTempSelectedTeams(new Set(selectedTeams));
    setTempSelectedPositions(new Set(selectedPositions));
    setTempSelectedAwards(new Set(selectedAwards)); // Add this line
    setTempIsActiveOnly(isActiveOnly);
    setTempDraftStatus(draftStatus);
    setTempMinGames(minGames);
  };
  const settingsDialog = (
    <Modal open={settingsOpen} onClose={() => setSettingsOpen(false)} wide>
      <h2 className={styles.modalTitle}>Filters</h2>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Positions</h3>
        <div className={styles.chipRow}>
          {["F", "G", "C", "G-F", "F-C"].map((pos) => (
            <label
              key={pos}
              className={`${styles.chip} ${tempSelectedPositions.has(pos) ? styles.chipActive : ""}`}
            >
              <input
                type="checkbox"
                checked={tempSelectedPositions.has(pos)}
                onChange={() => togglePositionSelection(pos)}
              />
              {pos}
            </label>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Teams</h3>
        <div className={styles.filterRow}>
          {teams.map((team, index) => (
            <button
              key={index}
              onClick={() => toggleTeam(team)}
              className={`${styles.filterButton} ${selectedTeams.has(team) ? styles.filterButtonSelected : ""}`}
            >
              <img src={teamLogos[team]} alt={team} className={styles.teamLogo2} />
              {team}
            </button>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Player Status</h3>
        <div className={styles.segmented}>
          {[
            ["active", "Active"],
            ["inactive", "Inactive"],
            ["both", "Both"],
          ].map(([val, label]) => (
            <label
              key={val}
              className={`${styles.segment} ${tempIsActiveOnly === val ? styles.segmentActive : ""}`}
            >
              <input
                type="radio"
                name="player-status"
                value={val}
                checked={tempIsActiveOnly === val}
                onChange={(e) => setTempIsActiveOnly(e.target.value)}
              />
              {label}
            </label>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Draft Status</h3>
        <div className={styles.segmented}>
          {[
            ["drafted", "Drafted"],
            ["undrafted", "Undrafted"],
            ["both", "Both"],
          ].map(([val, label]) => (
            <label
              key={val}
              className={`${styles.segment} ${tempDraftStatus === val ? styles.segmentActive : ""}`}
            >
              <input
                type="radio"
                name="draft-status"
                value={val}
                checked={tempDraftStatus === val}
                onChange={(e) => setTempDraftStatus(e.target.value)}
              />
              {label}
            </label>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Minimum Games Played</h3>
        <input
          type="number"
          min={0}
          className={styles.input}
          value={tempMinGames}
          onChange={(e) => setTempMinGames(e.target.value)}
        />
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Awards</h3>
        <div className={styles.chipRow}>
          {awards.map((award, index) => (
            <label
              key={index}
              className={`${styles.chip} ${tempSelectedAwards.has(award) ? styles.chipActive : ""}`}
            >
              <input
                type="checkbox"
                checked={tempSelectedAwards.has(award)}
                onChange={() => toggleAwardSelection(award)}
              />
              {award}
            </label>
          ))}
        </div>
      </section>

      <div className={styles.modalActions}>
        <button onClick={saveSettings} className={styles.btnPrimary}>
          Save Settings
        </button>
      </div>
    </Modal>
  );

  const revealHint = () => {
    const newHints = [...hintsGiven];

    if (newHints.length === 0 && currentPlayer) {
      newHints.push(currentPlayer.Pos); // First hint: Position
    } else if (newHints.length === 1 && currentPlayer) {
      // Determine the draft detail or undrafted status based on the presence of a draft year
      const draftDetail =
        currentPlayer.Round && currentPlayer.Pick
          ? `Round: ${currentPlayer.Round}, Pick: ${currentPlayer.Pick}, Year: ${currentPlayer.DrftYr}`
          : `Undrafted, Started Playing: ${currentPlayer.From}`;

      newHints.push(draftDetail);
    }
    setHintsGiven(newHints);
  };

  const playerStats = {
    AS: currentPlayer?.AS || "N/A",
    MP: currentPlayer?.MP || "N/A",
    FG: currentPlayer?.FG || "N/A",
    FGA: currentPlayer?.FGA || "N/A",
    "2P": currentPlayer?.["2P"] || "N/A",
    "2PA": currentPlayer?.["2PA"] || "N/A",
    "3P": currentPlayer?.["3P"] || "N/A",
    "3PA": currentPlayer?.["3PA"] || "N/A",
    FT: currentPlayer?.FT || "N/A",
    FTA: currentPlayer?.FTA || "N/A",
    ORB: currentPlayer?.ORB || "N/A",
    DRB: currentPlayer?.DRB || "N/A",
    TRB: currentPlayer?.TRB || "N/A",
    AST: currentPlayer?.AST || "N/A",
    STL: currentPlayer?.STL || "N/A",
    BLK: currentPlayer?.BLK || "N/A",
    TOV: currentPlayer?.TOV || "N/A",
    PF: currentPlayer?.PF || "N/A",
    PTS: currentPlayer?.PTS || "N/A",
    "FG%": currentPlayer?.["FG%"] || "N/A",
    "2P%": currentPlayer?.["2P%"] || "N/A",
    "3P%": currentPlayer?.["3P%"] || "N/A",
    "FT%": currentPlayer?.["FT%"] || "N/A",
    "TS%": currentPlayer?.["TS%"] || "N/A",
    "eFG%": currentPlayer?.["eFG%"] || "N/A",
  };

  // Function to pick two random stats from an object where values are not null
  const pickRandomStats = (stats) => {
    const keys = Object.keys(stats).filter(
      (key) => stats[key] != null && stats[key] !== undefined
    ); // Ensure that both null and undefined are filtered out
    const randomKeys = keys.sort(() => 0.5 - Math.random()).slice(0, 2); // Randomly pick two keys
    return randomKeys.map((key) => ({ key, value: stats[key] }));
  };

  // Always shown stats
  const alwaysShowStats = {
    "Games Played": currentPlayer?.G || "N/A",
    "Games Started": currentPlayer?.GS || "N/A",
  };

  // Getting random stats
  const randomStats = pickRandomStats(playerStats);

  const postStreakToLeaderboard = async (playerID, streak) => {
    try {
      const response = await fetch(
        "https://us-central1-crossover-grid-511a4.cloudfunctions.net/api/updateLeaderboard",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ playerID, score: streak }),
        }
      );
      const responseData = await response.json();
      if (response.ok) {
        toast.success("Streak updated on leaderboard!");
      } else {
        throw new Error(responseData.message || "Failed to update streak");
      }
    } catch (error) {
      toast.error("Error updating leaderboard: " + error.message);
    }
  };

  const getRandomPlayer = () => {
    return playerData[Math.floor(Math.random() * playerData.length)];
  };

  // Fetch player only when mode or currentDate changes.
  useEffect(() => {
    fetchPlayer(mode);
  }, [mode, currentDate]);
  // Handle changes in settings for "Unlimited" mode separately
  useEffect(() => {
    if (mode === "Unlimited") {
      fetchPlayer("Unlimited");
    }
  }, [mode, selectedPositions, isActiveOnly, selectedTeams, selectedAwards]);

  // Fetch the player data on component mount
  const handleGuessChange = (event) => {
    const value = event.target.value;
    setUserGuess(value);

    const trimmedValue = value.trim().toLowerCase(); // Prepare the input for comparison

    if (trimmedValue.length > 0) {
      // Create a map to accumulate players by name
      const playersByName = new Map();
      playerData.forEach((player) => {
        const playerName = player.Player.toLowerCase();
        if (playerName.includes(trimmedValue)) {
          if (!playersByName.has(player.Player)) {
            playersByName.set(player.Player, []);
          }
          playersByName.get(player.Player).push(player);
        }
      });

      const filteredSuggestions = Array.from(playersByName).map(
        ([name, players]) => ({
          Player: name,
          ids: players.map((p) => p.PlayerId), // Store all relevant IDs
          details: players, // You could store additional details here if needed
        })
      );

      setSuggestions(filteredSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const selectPlayer = (selectedName) => {
    const playerInfo = suggestions.find((s) => s.Player === selectedName);
    if (playerInfo.ids.length === 1) {
      setCurrentPlayerId(playerInfo.ids[0]); // Setting the ID from the single ID found
    } else {
      // You might need logic here to handle multiple IDs
      // For now, let's just set the first one
      setCurrentPlayerId(playerInfo.ids[0]);
    }
    setUserGuess(selectedName);
    setSuggestions([]);
  };


  const handleGuessSubmit = (event) => {
    event.preventDefault();
    toast.dismiss();
    if (mode === "Unlimited" || (mode === "Daily" && !playedToday)) {
      const isCorrectGuess =
        currentPlayer &&
        userGuess.toLowerCase() === currentPlayer.Player.toLowerCase();
      if (isCorrectGuess) {
        // Correct Guess
        setSeenPlayerIDs(
          (prev) => new Set(prev.add(currentPlayer["Player-additional"]))
        ); // Update seenPlayerIDs when a correct guess is made
        toast.success(
          `${currentPlayer.Player} was correct! Guessed in ${3 - guessesLeft + 1} tries!`
        );
        endGame(true);
      } else {
        // Incorrect Guess
        if (guessesLeft > 1) {
          setGuessesLeft(guessesLeft - 1);
          toast.error(`Incorrect guess: ${userGuess}. Try again!`);
          revealHint();
        } else {
          // No guesses left, end the game
          endGame(false);
        }
      }
      setUserGuess("");
    }
  };
  const endGame = (gameWon) => {
    updateGamesPlayed();
    setGuessesLeft(3);
    setHintsGiven([]);
    setUserGuess("");

    if (gameWon) {
      updateWinsAndStreak();
      // Potential logic to handle a win situation
    } else {
      // Game ends because of an incorrect guess
      const currentStreak =
        mode === "Daily" ? homeWinStreak : unlimitedWinStreak;
      if (currentStreak >= 5) {
        // Only show the streak modal if the game ends due to a wrong guess and the streak was 5 or more
        setShowStreakModal(true);
        // Defer streak reset until modal is dismissed
      } else {
        // Reset streak immediately if it was less than 5
        resetStreak();
      }
      toast.error(
        `No more guesses left! The correct player was ${currentPlayer.Player}.`
      );
    }

    setPlayedToday(mode === "Daily");
    if (mode === "Unlimited" || !gameWon) {
      fetchPlayer(mode); // Fetch a new player for Unlimited or after a loss.
    }

    if (mode === "Daily") {
      setPlayedToday(true);
      localStorage.setItem(currentDate, "played");
    }
  };

  const NoPlayersAvailableModal = ({ open, onClose, onShare, onRestart }) => {
    return (
      <Modal open={open} onClose={onClose}>
        <h2 className={styles.modalTitle}>No More Players Available</h2>
        <p className={styles.modalLead}>There are no more players available under the current filters.</p>
        <div className={styles.modalActions}>
          <button className={styles.btnGhost} onClick={onClose}>Close</button>
          <button className={styles.btnPrimary} onClick={onShare}>Share Achievement</button>
          <button className={styles.btnPrimary} onClick={onRestart}>Restart Game</button>
        </div>
      </Modal>
    );
  };
  const StreakModal = ({ open, onClose }) => {
    const currentStreak = mode === "Daily" ? homeWinStreak : unlimitedWinStreak;
    return (
      <Modal open={open} onClose={onClose}>
        <h2 className={styles.modalTitle}>Congratulations!</h2>
        <p className={styles.modalLead}>You have achieved a streak of {currentStreak} correct guesses!</p>
        <p className={styles.modalLead}>Your streak has been added to the leaderboard.</p>
        <div className={styles.modalActions}>
          <button
            className={styles.btnPrimary}
            onClick={() => {
              shareScore();
              onClose();
            }}
          >
            Share on Twitter
          </button>
          <button className={styles.btnGhost} onClick={onClose}>Close</button>
        </div>
      </Modal>
    );
  };

  const handleResetGame = () => {
    setSeenPlayerIDs(new Set());
    setFiltersToDefault(); // Reset all filters to default state or as you define
    fetchPlayer("Unlimited"); // Fetch players again after resetting
    setShowNoPlayersModal(false); // Close the modal
  };

  const handleShareAchievement = () => {
    // Assume that player count is being tracked somewhere in state
    shareAchievement(seenPlayerIDs.size);
    setShowNoPlayersModal(false); // Optionally close the modal after sharing
  };
  const handleStreakModalClose = () => {
    resetStreak(); // This will reset the streak to 0
    setShowStreakModal(false); // Close the modal
  }; // Stat pools

  const setFiltersToDefault = () => {
    setSelectedPositions(new Set()); // Assuming no positions are selected by default
    setSelectedTeams(new Set()); // Assuming no teams are selected by default
    setIsActiveOnly("both"); // Assuming the default is to show both active and inactive players
    setDraftStatus("both"); // Assuming the default is to show both drafted and undrafted players
    setMinGames(0); // Assuming the default is no minimum games played
    setSelectedAwards(new Set()); // Reset selected awards to an empty set

    // Reset any additional filter states here as needed
  };

  useEffect(() => {
    // Show modal only in Unlimited mode and when there are no more players to guess
    if (mode === "Unlimited" && outOfPlayers && filteredPlayers.length === 0) {
      setShowNoPlayersModal(true);
    } else {
      setShowNoPlayersModal(false);
    }
  }, [mode, outOfPlayers, filteredPlayers.length]);

  useEffect(() => {
    localStorage.setItem("homeTotalGames", homeTotalGames);
    localStorage.setItem("homeTotalWins", homeTotalWins);
    localStorage.setItem("homeWinStreak", homeWinStreak);

    localStorage.setItem("unlimitedTotalGames", unlimitedTotalGames);
    localStorage.setItem("unlimitedTotalWins", unlimitedTotalWins);
    localStorage.setItem("unlimitedWinStreak", unlimitedWinStreak);
  }, [
    homeTotalGames,
    homeTotalWins,
    homeWinStreak,
    unlimitedTotalGames,
    unlimitedTotalWins,
    unlimitedWinStreak,
  ]);

  const shareScore = () => {
    const currentStreak = mode === "Daily" ? homeWinStreak : unlimitedWinStreak;
    const filtersDescription = formatFilters();

    const tweetText = `I guessed ${currentStreak} players under the current filters in Guess the Player!  Filters used: ${filtersDescription}.`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(url, "_blank");
  };
  {
    showHowToPlay && (
      <HowToPlayModal open={showHowToPlay} onClose={toggleHowToPlayModal} />
    );
  }

  useEffect(() => {
    setPlayedToday(localStorage.getItem(currentDate) === "played");
  }, [currentDate]);

  if (!currentPlayer) {
    return <div>Loading player data...</div>;
  }
  // Early return if today's challenge has been completed
  if (mode === "Daily" && playedToday) {
    const currentStreak = mode === "Daily" ? homeWinStreak : unlimitedWinStreak;
    const guessesUsed = 3 - guessesLeft + 1;
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.title}>
            Guess the Player
            {mode === "Unlimited" && (
              <button
                type="button"
                onClick={() => handleOpenSettings(true)}
                className={styles.iconBtn}
                title="Filter settings"
                style={{ marginLeft: 8, verticalAlign: "middle" }}
              >
                <Icon.filter />
              </button>
            )}
          </div>

          <p>You've already played today's challenge. Here's the result:</p>
          <p>The correct player was: {currentPlayer.Player}</p>

          <button onClick={handleModeChange} className={styles.toggleButton}>
            Switch to Unlimited
          </button>

          {/* Share button for Daily mode */}
          <button
            onClick={() => {
              const tweetText = `I guessed the @Daily correct player in ${guessesUsed} guess${guessesUsed > 1 ? "es" : ""}! My win streak is ${currentStreak}! `;
              const tweetUrl = "1782399386833616947"; // Only include the tweet ID
              const encodedTweetText = encodeURIComponent(tweetText);
              const encodedTweetUrl = encodeURIComponent(tweetUrl);
              const url = `https://twitter.com/intent/tweet?text=${encodedTweetText}&in_reply_to=${encodedTweetUrl}`;
              window.open(url, "_blank");
            }}
          >
            Share result on Twitter
          </button>

          {/* Copy button for Daily mode */}
          <button
            onClick={() => {
              navigator.clipboard.writeText(
                `I guessed the correct player in ${guessesUsed} guess${guessesUsed > 1 ? "es" : ""}! My win streak is ${currentStreak}! Can you beat me?`
              );
            }}
          >
            Copy result to Clipboard
          </button>
        </div>
      </div>
    );
  }
  const teamHeaderText =
    currentPlayer.Team.length > 1 ? "Teams Played For" : "Team Played For"; // This could be singular if only one team

  return (
    <div className="app">
      <ToastContainer position="top-center" limit={2} />

      <div className={styles.appContainer}>
        <nav className="navbar">
          {/* Navbar content */}
          <a
            href="https://crossovergrid.com"
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "flex",
              alignItems: "center", // This ensures vertical alignment
              justifyContent: "center", // This centers the content horizontally if the a-tag is a flex container
            }}
          >
            <img src={crossover_logo} alt="Logo" className="navbar-logo" />
            <p
              style={{
                margin: 0, // Removes default margin that might offset alignment
                fontWeight: "bold",
                fontSize: 20,
                display: "flex", // Makes the paragraph itself a flex container
                alignItems: "center", // Centers the text inside the p-tag vertically
                justifyContent: "center", // Centers the text inside the p-tag horizontally
              }}
            >
              CROSSOVER NBA
            </p>
          </a>
          <div className={styles.navActions}>
            <Link to="/" title="Home" className={styles.iconBtn}>
              <Icon.home />
            </Link>
            <button
              type="button"
              onClick={() => handleOpenSettings(true)}
              className={styles.iconBtn}
              title="Settings"
            >
              <Icon.settings />
            </button>
          </div>
        </nav>
      </div>
      {settingsDialog}

      <div className="sport-picker">
        {/* Sport picker content */}
        <div className="links">
          {sports.map((sport) => (
            <Link
              key={sport.id}
              className={`sportLink ${sport.id === "21" ? "prime-gaming-logo" : ""} ${page_name === sport.id ? "selected" : ""}`}
              to={sport.path}
            >
              {sport.emoji.startsWith("http") ? (
                <img src={sport.emoji} alt={sport.name} />
              ) : (
                <span>{sport.emoji}</span>
              )}
              <br />
              {sport.name.length < 4 ? (
                <span>{sport.name}</span>
              ) : (
                <span style={{ fontSize: 12 }}>{sport.name}</span>
              )}
            </Link>
          ))}
        </div>
      </div>
      <button onClick={handleModeChange}>
        {mode === "Daily" ? "Switch to Unlimited" : "Switch to Daily"}
      </button>

      <div className={styles.container}>
        <div className={styles.card}>
          <h1 className={styles.title}>
            Guess the Player
            <button
              type="button"
              onClick={toggleHowToPlayModal}
              className={styles.iconBtn}
              title="How to Play"
              style={{ marginLeft: 8 }}
            >
              <Icon.help />
            </button>
            {showHowToPlay && (
              <HowToPlayModal
                open={showHowToPlay}
                onClose={toggleHowToPlayModal}
              />
            )}
          </h1>
          {showEditPlayerIDModal && (
            <EditPlayerIDModal
              open={showEditPlayerIDModal}
              onClose={toggleEditPlayerIDModal}
            />
          )}
          {showNoPlayersModal && (
            <NoPlayersAvailableModal
              open={showNoPlayersModal}
              onClose={() => setShowNoPlayersModal(false)}
              onShare={handleShareAchievement}
              onRestart={handleResetGame}
            />
          )}
          {showStreakModal && (
            <StreakModal
              open={showStreakModal}
              onClose={handleStreakModalClose} // Ensure this handles all close actions
            />
          )}
          <div className={styles.controls}>
            {mode === "Unlimited" && (
              <>
                <button
                  type="button"
                  onClick={() => handleOpenSettings(true)}
                  className={styles.iconBtn}
                  title="Filter settings"
                  style={{ marginRight: 8 }}
                >
                  <Icon.settings />
                </button>
                <button
                  type="button"
                  onClick={toggleEditPlayerIDModal}
                  className={styles.iconBtn}
                  title="Edit Player ID"
                  style={{ marginRight: 8 }}
                >
                  <Icon.edit />
                </button>
                <span className={styles.availablePlayersBox}>
                  Available Players: {filteredPlayers.length}
                </span>
                <button
                  type="button"
                  onClick={openLeaderboardModal}
                  className={styles.btnPrimary}
                  style={{ marginLeft: 10 }}
                >
                  Show Leaderboard
                </button>
              </>
            )}
          </div>
          <div className={styles.toggleMode}></div>
          <div className={styles.scoreboard}>
            <p>
              🏆 Win Percentage:{" "}
              {mode === "Daily"
                ? homeTotalGames
                  ? ((homeTotalWins / homeTotalGames) * 100).toFixed(2)
                  : 0
                : unlimitedTotalGames
                  ? ((unlimitedTotalWins / unlimitedTotalGames) * 100).toFixed(
                      2
                    )
                  : 0}
              %
            </p>
            <p>
              🎮 Total Games:{" "}
              {mode === "Daily" ? homeTotalGames : unlimitedTotalGames}
            </p>
            <p>
              🔥 Win Streak:{" "}
              {mode === "Daily" ? homeWinStreak : unlimitedWinStreak}
            </p>
            <p>
              ⭐ Highest Streak:{" "}
              {mode === "Daily" ? homeHighestStreak : unlimitedHighestStreak}
            </p>

            {mode === "Unlimited" && guessesLeft === 0 && (
              <button onClick={shareScore} className={styles.shareButton}>
                Share on Twitter
              </button>
            )}
          </div>{" "}
          <div>
            <LeaderboardModal
              open={showLeaderboardModal}
              onClose={closeLeaderboardModal}
              leaderboard={leaderboard}
              fetchLeaderboard={fetchLeaderboard}
            />
          </div>
          {/* Form for player guesses */}
          <form onSubmit={handleGuessSubmit} className={styles.guessForm}>
            <div className={styles.formControls}>
              <input
                type="text"
                value={userGuess}
                onChange={handleGuessChange}
                className={styles.guessInput}
                placeholder="Enter player's name"
              />
              <button type="submit" className={styles.guessButton}>
                Guess
              </button>
            </div>
            {suggestions.length > 0 && (
              <ul className={styles.suggestions}>
                {suggestions.map((suggestion, index) => (
                  <li
                    key={index}
                    onClick={() => selectPlayer(suggestion.Player)}
                    className={styles.suggestionItem}
                  >
                    {suggestion.Player}
                  </li>
                ))}
              </ul>
            )}
          </form>
          <div className={styles.guessesLeft}>Guesses Left: {guessesLeft}</div>
          {hintsGiven.map((hint, index) => {
            return (
              <div key={index} className={`${styles.infoBlock} ${styles.hint}`}>
                <p className={styles.info}>
                  {index === 0 ? "Position" : "Draft Details"}
                </p>
                <div className={styles.infoValue}>{hint}</div>
              </div>
            );
          })}
          <div className={styles.infoContainer}>
            {" "}
            {/* Container for both info blocks */}
            {currentPlayer.Born && (
              <div key="born" className={styles.infoBlock}>
                <p className={styles.info}>Born in {currentPlayer.Born}</p>
                <div className={styles.infoValue}>{currentPlayer.Born}</div>
              </div>
            )}
            {currentPlayer.College && (
              <div key="college" className={styles.infoBlock}>
                <p className={styles.info}>
                  {currentPlayer.Drafted
                    ? "College Drafted From"
                    : "Last College Played For"}
                </p>
                <div className={styles.infoValue}>
                  <>
                    <img
                      src={`https://crossover-grid.s3.amazonaws.com/college-logos/${currentPlayer.College.toLowerCase().replace(/[()&]/g, "").replace(/ /g, "-")}.png`}
                      alt={currentPlayer.College}
                      className={styles.collegeLogo}
                    />
                    {currentPlayer.College}
                  </>
                </div>
              </div>
            )}
            {!currentPlayer.Born && !currentPlayer.College && (
              <div key="unknown" className={styles.infoBlock}>
                <p className={styles.info}>Location Unknown</p>
              </div>
            )}
          </div>
          {hasDraftDetails && currentPlayer && (
            <div className={styles.infoBlock}>
              <p className={styles.info}>Team Drafted By</p>
              <img
                src={
                  teamLogos[currentPlayer?.Drafted || currentPlayer.Team?.[0]]
                }
                alt={currentPlayer?.Drafted || currentPlayer.Team?.[0]}
                className={styles.teamLogo}
              />
            </div>
          )}
          {/* Teams played for block */}
          <div className={styles.infoBlock}>
            <p className={styles.info}>{teamHeaderText}</p>
            <div className={styles.teamLogos}>
              {currentPlayer.Team.map(
                (
                  team // Map through all teams without slicing
                ) => (
                  <img
                    src={teamLogos[team]}
                    key={team}
                    alt={team}
                    className={styles.teamLogo}
                  />
                )
              )}
            </div>
          </div>
          <div className={styles.infoBlock}>
            <p className={styles.info}>Career Info</p>
            {Object.entries(alwaysShowStats).map(([stat, value]) => (
              <p key={stat}>{`${stat}: ${value}`}</p>
            ))}
            {randomStats.map((stat) => (
              <p key={stat.key}>{`${stat.key}: ${stat.value}`}</p>
            ))}
            <div
              dangerouslySetInnerHTML={createMarkup(
                formatCareerAwards(currentPlayer)
              )}
            />{" "}
          </div>
          <div className={styles.inputGroup}>
            <div className={styles.input}>
              <strong>Player ID:</strong> {playerIDInput || "No Player ID set"}
            </div>
          </div>
          <div>Available players: {filteredPlayers.length}</div>
        </div>
      </div>
    </div>
  );
};

export default GuessThePlayer;
